/**
 * useRealtimeTable — the canonical Realtime subscription hook.
 *
 * **Part of Phase 2 of the Realtime ↔ Mycelium plan** (consolidation).
 *
 * Before this hook, the repo accumulated ~10 bespoke `useRealtime*` hooks that
 * each re-implemented the same pattern: open a Supabase channel → listen for
 * `postgres_changes` → invalidate one or more TanStack Query keys. The pattern
 * leaked across `useRealtimeClients`, `useRealtimeNotifications`,
 * `useRealtimeActivities`, `useRealtimeTimeBlocks`, `useRealtimeBookings`,
 * `useRealtimeTasks` (×2), `useRobustRealtimeTasks`,
 * `useRealtimeTaskActivities`, and `useRealtimeSalesActivities`.
 *
 * This hook is the **single consolidation point**. It is backed by
 * `unified-realtime.service.ts` (no direct `supabase.channel()` calls), so
 * every subscription benefits from the service's de-duplication, channel
 * reuse, connection-health tracking, and metrics.
 *
 * ### The rule (see `docs/protocols/mycelium/REALTIME_VS_MYCELIUM.md`)
 * Realtime is for UI reflexes. If your callback does anything beyond
 * `queryClient.invalidateQueries` / updating local component state, you want
 * a **Mycelium event from a Postgres trigger**, not a Realtime subscription.
 *
 * ### Usage
 *
 * ```tsx
 * // Simple: invalidate a query key on any change to `todos`.
 * useRealtimeTable('todos', {
 *   invalidateKeys: [['todos']],
 * });
 *
 * // Scoped by organization and limited to inserts.
 * useRealtimeTable('invoices', {
 *   filter: { column: 'organization_id', operator: 'eq', value: orgId },
 *   events: ['INSERT'],
 *   invalidateKeys: [['invoices', orgId]],
 * });
 *
 * // Custom callback (still just a UI reflex — no side effects!).
 * useRealtimeTable('clients', {
 *   onChange: (payload) => {
 *     if (payload.eventType === 'DELETE') setShowToast(true);
 *   },
 * });
 * ```
 *
 * @module useRealtimeTable
 * @since 2026-04-17 (Phase 2)
 */

import { useService } from "@/contexts/ServiceContext";
import type {
  IUnifiedRealtimeService,
} from "@/services/admin/unified-realtime.service";
import type {
  RealtimeCallback,
  RealtimeFilter,
  RealtimePayload,
  RealtimeTableMap,
  SubscriptionConfig,
} from "@/types/realtime";
import { logger } from "@/lib/logger";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

// ============================================================================
// PUBLIC API
// ============================================================================

export type RealtimeEventType = "INSERT" | "UPDATE" | "DELETE";

export interface UseRealtimeTableOptions<
  TTable extends keyof RealtimeTableMap,
> {
  /**
   * When `false`, the hook does not subscribe. Useful for gating on auth or
   * feature flags without conditionally calling the hook.
   * @default true
   */
  enabled?: boolean;

  /**
   * Optional server-side filter. Narrows which row changes the subscription
   * receives (e.g. scope by `organization_id`).
   */
  filter?: RealtimeFilter;

  /**
   * Subset of event types to receive. Omit to receive all three.
   */
  events?: RealtimeEventType[];

  /**
   * Client-side debounce applied by `unified-realtime.service` before
   * invoking `onChange`. Useful for noisy tables.
   */
  debounceMs?: number;

  /**
   * Dedup window used by the unified service's `MessageDeduplicator`. A
   * matching `(table, eventType, recordId)` within this many ms is dropped.
   */
  deduplicationWindow?: number;

  /**
   * TanStack Query keys to invalidate on any delivered change. Called via
   * `queryClient.invalidateQueries({ queryKey })`. Most Realtime subscribers
   * only need this — plain cache invalidation.
   */
  invalidateKeys?: QueryKey[];

  /**
   * Imperative callback invoked for every delivered change. Keep this
   * side-effect-free (UI reflexes only). For business logic, publish a
   * Mycelium event from a Postgres trigger instead.
   */
  onChange?: RealtimeCallback<RealtimeTableMap[TTable]>;
}

export interface UseRealtimeTableResult {
  /** True once the unified service has accepted the subscription. */
  isSubscribed: boolean;
  /** Most recent subscription/setup error message, if any. */
  error: string | null;
}

// ============================================================================
// IMPLEMENTATION
// ============================================================================

/**
 * Canonical Realtime subscription hook. Call this instead of adding a new
 * bespoke `useRealtime*` hook.
 *
 * @typeParam TTable - One of the keys of `RealtimeTableMap`.
 */
export function useRealtimeTable<TTable extends keyof RealtimeTableMap>(
  table: TTable,
  options: UseRealtimeTableOptions<TTable> = {},
): UseRealtimeTableResult {
  const {
    enabled = true,
    filter,
    events,
    debounceMs,
    deduplicationWindow,
    invalidateKeys,
    onChange,
  } = options;

  const realtimeService =
    useService<IUnifiedRealtimeService>("unified-realtime");
  const queryClient = useQueryClient();

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the latest callback/keys in refs so subscription lifecycle is
  // unaffected by closure churn. Only the things that actually change the
  // channel identity (table/filter/events) should re-trigger subscribe.
  const onChangeRef = useRef<
    RealtimeCallback<RealtimeTableMap[TTable]> | undefined
  >(onChange);
  const invalidateKeysRef = useRef<QueryKey[] | undefined>(invalidateKeys);
  // Filter and events are also accessed via refs so callers can pass inline
  // object/array literals without thrashing the channel — only the
  // signatures (content-based) participate in the dep array.
  const filterRef = useRef<RealtimeFilter | undefined>(filter);
  const eventsRef = useRef<RealtimeEventType[] | undefined>(events);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    invalidateKeysRef.current = invalidateKeys;
  }, [invalidateKeys]);

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  // Stabilize the filter reference. Consumers typically build filters
  // inline, which would otherwise force a resubscribe every render.
  // We deliberately depend on the destructured fields rather than the
  // object identity so re-renders with equivalent filters are no-ops.
  const filterSignature = useMemo(
    () =>
      filter
        ? `${filter.column}|${filter.operator}|${String(filter.value)}`
        : "",
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional content-hash deps: depend on destructured fields, not the `filter` object identity
    [filter?.column, filter?.operator, filter?.value],
  );

  const eventsSignature = useMemo(
    () => (events ? [...events].sort().join(",") : ""),
    [events],
  );

  useEffect(() => {
    // Hard-disabled subscription — ensure clean state.
    if (!enabled) {
      setIsSubscribed(false);
      return;
    }

    // The unified service is registered asynchronously; if it isn't ready
    // yet, bail — React will re-run the effect when the context value flips.
    if (!realtimeService) {
      setError(null);
      setIsSubscribed(false);
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    // Read filter/events through refs so identity-only changes (inline
    // literals) don't tear down the channel.
    const currentFilter = filterRef.current;
    const currentEvents = eventsRef.current;
    const config: SubscriptionConfig = {
      ...(currentFilter ? { filter: currentFilter } : {}),
      ...(currentEvents ? { events: currentEvents } : {}),
      ...(debounceMs !== undefined ? { debounceMs } : {}),
      ...(deduplicationWindow !== undefined ? { deduplicationWindow } : {}),
    };

    const callback: RealtimeCallback<RealtimeTableMap[TTable]> = async (
      payload,
    ) => {
      try {
        // Invalidate any query keys the caller registered.
        const keys = invalidateKeysRef.current;
        if (keys && keys.length > 0) {
          for (const queryKey of keys) {
            queryClient.invalidateQueries({ queryKey });
          }
        }
        // Hand off to the user callback last so it sees a freshly invalidated
        // cache (not the other way around).
        await onChangeRef.current?.(payload);
      } catch (callbackError) {
        logger.error("❌ useRealtimeTable callback error", {
          table,
          eventType: payload.eventType,
          error:
            callbackError instanceof Error
              ? callbackError.message
              : String(callbackError),
        });
      }
    };

    (async () => {
      try {
        const response = await realtimeService.subscribeToTable(
          table,
          callback,
          config,
        );
        if (cancelled) {
          // Hook unmounted (or deps changed) before subscription resolved —
          // tear it down immediately.
          if (response.success && response.data) {
            response.data();
          }
          return;
        }
        if (!response.success) {
          setError(response.error ?? "Unknown subscription error");
          setIsSubscribed(false);
          return;
        }
        unsubscribe = response.data;
        setError(null);
        setIsSubscribed(true);
      } catch (subscribeError) {
        if (cancelled) {
          return;
        }
        setError(
          subscribeError instanceof Error
            ? subscribeError.message
            : "Unknown subscription error",
        );
        setIsSubscribed(false);
        logger.error("❌ useRealtimeTable subscribe failed", {
          table,
          error:
            subscribeError instanceof Error
              ? subscribeError.message
              : String(subscribeError),
        });
      }
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (unsubError) {
          logger.warn("⚠️ useRealtimeTable unsubscribe error", {
            table,
            error:
              unsubError instanceof Error
                ? unsubError.message
                : String(unsubError),
          });
        }
      }
      setIsSubscribed(false);
    };
    // filterSignature/eventsSignature are content-hashed deps — they re-run
    // the effect when the values actually change, but ignore identity-only
    // changes (inline object/array literals).
  }, [
    enabled,
    realtimeService,
    table,
    filterSignature,
    eventsSignature,
    debounceMs,
    deduplicationWindow,
    queryClient,
  ]);

  return { isSubscribed, error };
}

/**
 * Type re-export for convenience: `useRealtimeTable` callers sometimes want
 * to name the payload type in their own helpers.
 */
export type { RealtimePayload };
